import { doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';

import { db } from '~/config/firebase.config';

import { DEFAULT_PRIMARY, DEFAULT_SECONDARY, isHexColor } from './brandingColors';

/**
 * « Ma marque » (formule Pro) : logo + 2 couleurs d'un établissement, affichés sur
 * sa TV (barre du haut, cadres, coin de l'écran pendant les jeux) et sur le
 * téléphone des joueurs.
 *
 * Rangé dans `clients/{id}/public/branding` : le téléphone du joueur (non
 * connecté) ne lit que le sous-dossier `public`. Règles Firestore (dépôt
 * unboared-b2b) : écriture par les membres du lieu, mais `enabled: true`
 * seulement si `clients/{id}.plan == 'pro'` (champ écrit par stripe-b2b seul).
 * Hors Pro, le réglage s'enregistre (aperçu) sans s'afficher nulle part ; au
 * retour en formule standard, stripe-b2b repasse `enabled` à false.
 *
 * Le logo est une image `data:` (≤ ~300 Ko) dans le document même : pas de
 * Firebase Storage à ouvrir, une seule lecture côté téléphone.
 */

export type BrandCorner = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
export const BRAND_CORNERS: BrandCorner[] = ['top-right', 'top-left', 'bottom-right', 'bottom-left'];

export type ClientBranding = {
    logo: string | null;
    primary: string;
    secondary: string;
    corner: BrandCorner;
    enabled: boolean;
};

export const EMPTY_BRANDING: ClientBranding = {
    logo: null,
    primary: DEFAULT_PRIMARY,
    secondary: DEFAULT_SECONDARY,
    corner: 'top-right',
    enabled: false,
};

/**
 * ≤ ~100 Ko d'image : CHAQUE téléphone de la salle télécharge le logo au scan du QR
 * (240 joueurs × 300 Ko = 70 Mo sur le wifi du bar). Borne aussi vérifiée par les règles.
 */
export const MAX_LOGO_CHARS = 140_000;
/** Image `data:` complète (pas seulement le début) : la valeur finit aussi dans un `url("…")` CSS. */
const LOGO_DATA_URL = /^data:image\/(png|webp|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/;

const ref = (clientID: string) => doc(db, 'clients', clientID, 'public', 'branding');

export function brandingFromFirestore(data: Record<string, unknown> | undefined): ClientBranding {
    if (!data) return EMPTY_BRANDING;
    const logo =
        typeof data.logo === 'string' && LOGO_DATA_URL.test(data.logo) && data.logo.length <= MAX_LOGO_CHARS
            ? data.logo
            : null;
    return {
        logo,
        primary: isHexColor(data.primary) ? data.primary : DEFAULT_PRIMARY,
        secondary: isHexColor(data.secondary) ? data.secondary : DEFAULT_SECONDARY,
        corner: BRAND_CORNERS.includes(data.corner as BrandCorner) ? (data.corner as BrandCorner) : 'top-right',
        enabled: data.enabled === true,
    };
}

/** `exists` : le document existe déjà (une écriture partielle suffit alors, cf. writeClientBranding). */
export function subscribeClientBranding(
    clientID: string,
    onChange: (branding: ClientBranding, exists: boolean) => void,
    onError?: (error: unknown) => void
) {
    return onSnapshot(ref(clientID), (snap) => onChange(brandingFromFirestore(snap.data()), snap.exists()), onError);
}

/**
 * Enregistre SEULEMENT les champs modifiés (fusion) : ne réécrit ni `enabled` posé par le
 * serveur au passage en Pro, ni la modification d'un autre admin sur un autre champ.
 * Premier enregistrement : document complet (les règles exigent couleurs et coin).
 */
export function writeClientBranding(clientID: string, patch: Partial<ClientBranding>) {
    return setDoc(ref(clientID), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}

function loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('unreadable_image'));
        img.src = url;
    });
}

/** Rogne les bords transparents : un logo livré avec beaucoup de marge paraîtrait minuscule à l'écran. */
function trimTransparent(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;
    const { width, height } = canvas;
    const pixels = ctx.getImageData(0, 0, width, height).data;
    let top = height;
    let left = width;
    let right = -1;
    let bottom = -1;
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            if (pixels[(y * width + x) * 4 + 3] > 8) {
                if (x < left) left = x;
                if (x > right) right = x;
                if (y < top) top = y;
                if (y > bottom) bottom = y;
            }
        }
    }
    if (right < 0) return canvas;
    const out = document.createElement('canvas');
    out.width = right - left + 1;
    out.height = bottom - top + 1;
    out.getContext('2d')?.drawImage(canvas, left, top, out.width, out.height, 0, 0, out.width, out.height);
    return out;
}

/**
 * Fichier choisi (PNG, JPG, WebP, SVG) → image `data:` prête à enregistrer.
 * Redessinée dans un canevas : taille bornée, et un SVG devient une image
 * simple (aucun script embarqué ne passe).
 */
export async function logoFromFile(file: File): Promise<string> {
    const img = await loadImage(file);
    URL.revokeObjectURL(img.src);
    // Rogner d'abord (taille de travail bornée), réduire ensuite : sinon un logo livré avec
    // de grandes marges finirait minuscule après réduction.
    const naturalW = img.naturalWidth || 800;
    const naturalH = img.naturalHeight || 400;
    const work = Math.min(1, 4096 / Math.max(naturalW, naturalH));
    const full = document.createElement('canvas');
    full.width = Math.max(1, Math.round(naturalW * work));
    full.height = Math.max(1, Math.round(naturalH * work));
    full.getContext('2d')?.drawImage(img, 0, 0, full.width, full.height);
    const trimmed = trimTransparent(full);
    const steps: Array<[number, number]> = [
        [400, 0.8],
        [320, 0.75],
        [240, 0.7],
    ];
    for (const [maxSide, quality] of steps) {
        const scale = Math.min(1, maxSide / Math.max(trimmed.width, trimmed.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(trimmed.width * scale));
        canvas.height = Math.max(1, Math.round(trimmed.height * scale));
        canvas.getContext('2d')?.drawImage(trimmed, 0, 0, canvas.width, canvas.height);
        // WebP si le navigateur sait l'écrire (sinon il rend du PNG, accepté aussi).
        const url = canvas.toDataURL('image/webp', quality);
        if (url.length <= MAX_LOGO_CHARS && LOGO_DATA_URL.test(url)) return url;
    }
    throw new Error('logo_too_big');
}
